import { Dispatch, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router';
import classNames from 'classnames';
import type { Action } from 'redux';

import { OrderSideEnum } from '../../../../lib/enums/order-side.enum';
import { OrderStatusEnum } from '../../../../lib/enums/order-status.enum';
import { OrderTypeEnum } from '../../../../lib/enums/order-type.enum';
import lang from '../../../../lib/language';
import { SOROrderTimeInForce } from '../../../../lib/models/gateway/types';
import { Order } from '../../../../lib/models/trading/types';
import { getSummary } from '../../../../lib/store/ams/index';
import {
  getBuyingPowerFromOMS as getBuyingPowerFromOMSCall,
} from '../../../../lib/store/common-actions';
import { RootState } from '../../../../lib/store/index';
import {
  getAccountTradeHistory,
  getOpenPositions as getPositions,
  setHasOrderUpdate,
} from '../../../../lib/store/reporting';
import { PENDING_ORDERS_REQUEST_TENNSTATUS } from '../../../../lib/store/reporting/constants';
import { AccountTradeHistoryPayloadData } from '../../../../lib/store/reporting/types';
import {
  modifyOrder as serviceModifyOrder,
  resetRequestStatus,
} from '../../../../lib/store/trading/index';
import MarketStateCache from '../../../../lib/store-util/MarketStateCache';
import SubscribeGroupCache from '../../../../lib/store-util/SubscribeGroupCache';
import SymbolStatusCache from '../../../../lib/store-util/SymbolStatusCache';
import { checkIfSymbolIsHalted } from '../../../../lib/util/HaltSymbolHelper';
import { isMarketClosed } from '../../../../lib/util/MarketDataHelpers';
import { isMessageSummaryAffectingStatus } from '../../../../lib/util/trading-status-helpers';
import {
  convertOrderSideToText,
  mapOrderSORtoSide,
  orderStatusToCallStatus,
} from '../../../../lib/util/TradingHelpers';

import BuyAndSellDot from '../../../../assets/img/BuyAndSellDot.png';
import DeleteIcon from '../../../../assets/img/delete.png';
import EditIcon from '../../../../assets/img/edit_pen.png';
import { useIsMobile, useMaintenanceMode, useRowActionsMenu } from '../../../../hooks';
import BaseModal from '../../../../shared_components/BaseModal/BaseModal';
import Button from '../../../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../../../shared_components/Button/Button.types';
import ClickableDiv from '../../../../shared_components/ClickableDiv/ClickableDiv';
import ClickableImage from '../../../../shared_components/ClickableImage/ClickableImage';
import { UI_ROUTES } from '../../../../util/routes';
import CancelOrder from '../../../OrderActions/CancelOrder/CancelOrder';
import HaltedSymbol from '../../../OrderActions/HaltedSymbol/HaltedSymbol';
import ModifyOrder from '../../../OrderActions/ModifyOrder/ModifyOrder';
import {
  getNameByOrderType,
  modifyOrderPopUpBody,
  modifyOrderPopUpTitle,
  proccessPartiallyRejectedMessage,
} from '../../../OrderActions/OrderHelpers';
import useCancelOrderTimer from '../../../OrderActions/useCancelOrderTimer';
import useNewOrderTimer from '../../../OrderActions/useNewOrderTimer';
import useRefreshBuyingPower from '../../../OrderActions/useRefreshBuyingPower';
import ProcessFinishedModal from '../../../ProcessFinishedModal/ProcessFinishedModal';

import styles from './ButtonsCancelAndModify.module.scss';

type Props = {
  isSwipeable?: boolean;
  row: any;
  showModifyButtons?: boolean;
} & ReduxProps;

const ButtonsCancelAndModify = (props: Props) => {
  const {
    row,
    isSwipeable,
    requestStatus,
    submittedOrder,
    showModifyButtons,
    getOrders,
    getOpenPositions,
    modifyOrder,
    myStocksUpdate,
    resetStatus,
    getMessageSummary,
    getBuyingPowerFromOMS,
  } = props;

  const { isMobile } = useIsMobile();
  const isMaintenance = useMaintenanceMode();
  const { isOpen: isMenuOpen, menuStyle, openMenu, closeMenu } = useRowActionsMenu();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order>({});
  const [orderType, setOrderType] = useState<string>('');
  const [isHalted, setIsHalted] = useState<boolean>(false);
  const [orderSide, setOrderSide] = useState<OrderSideEnum>(OrderSideEnum.Buy);
  const [showModifyOrder, setShowModifyOrder] = useState<boolean>(false);
  const [showModalCancel, setShowModalCancel] = useState<boolean>(false);
  const [showResultModifyModal, setShowResultModifyModal] = useState<boolean>(false);
  const [showResultModalCancel, setShowResultModalCancel] = useState<boolean>(false);

  const { marketState } = MarketStateCache.use();
  const marketClosed = isMarketClosed(marketState);
  const { id, name, side, plainType, totalQuantity, partialFillQuantity, initialPrice } = row;
  const symbolsStatusCache = SymbolStatusCache.use(name);
  const { status, ordType, side: ordSide, orderQty, filledSoFar } = submittedOrder;
  const finalRejectedOrder = requestStatus === OrderStatusEnum.FinalReject;
  const orderInProgress = requestStatus === OrderStatusEnum.InProgress;
  const partiallyRejectedOrder = status === OrderStatusEnum.PartiallyReject;
  const showResultModal = [OrderStatusEnum.Accepted, OrderStatusEnum.Rejected].includes(
    requestStatus,
  );

  const { orderTimeoutPassed, showFillOrder, hasNoResponse } = useNewOrderTimer(
    showResultModifyModal && showResultModal,
    OrderTypeEnum.Limit,
    status,
    requestStatus,
  );
  const { cancelOrderTimeoutSuccess, hasNoCancelResponse } = useCancelOrderTimer(
    showResultModalCancel && showResultModal,
    status,
    requestStatus,
  );
  const rejectedOrder = requestStatus === OrderStatusEnum.Rejected;
  const showModifyOrderModal = orderTimeoutPassed
    || (hasNoResponse && showResultModifyModal)
    || (finalRejectedOrder && showResultModifyModal)
    || (rejectedOrder && showResultModifyModal && showResultModal);
  const showCancelOrderModal = cancelOrderTimeoutSuccess
    || (hasNoCancelResponse && showResultModalCancel)
    || (finalRejectedOrder && showResultModalCancel)
    || (rejectedOrder && showResultModalCancel && showResultModal);

  const onCancelClicked = () => {
    setShowModalCancel(true);
    SubscribeGroupCache.setComponent('initial');
  };

  const handleModalModify = () => {
    if (checkIfSymbolIsHalted(symbolsStatusCache)) {
      setIsHalted(true);
      return;
    }
    setOrderType(plainType);
    setOrderSide(mapOrderSORtoSide(side));

    if (isMobile) {
      navigate(
        {
          pathname: `${UI_ROUTES.modify}`,
        },
        {
          state: {
            symbol: name,
            parentOrderId: id,
            orderSide: convertOrderSideToText(mapOrderSORtoSide(side)),
            orderType: plainType,
            initialPrice,
            tradingSessionId: row.tradingSessionId,
            initialTimeInForce: SOROrderTimeInForce[row.timeInForce],
            orderQuantity: totalQuantity - partialFillQuantity,
            origin: window.location.pathname,
          },
          replace: true,
        },
      );
    } else {
      setShowModifyOrder(true);
    }
  };

  useRefreshBuyingPower(showModifyOrderModal, getBuyingPowerFromOMS);

  useEffect(() => {
    if (showResultModifyModal) {
      modifyOrder(order);
    }
  }, [modifyOrder, order, showResultModifyModal]);

  const handleModalCancel = (isConfirmed) => {
    if (isConfirmed) {
      setShowResultModalCancel(true);
    }
    SubscribeGroupCache.setComponent('initial');
    setShowModalCancel(false);
  };

  const handleCloseModifyModal = () => {
    SubscribeGroupCache.setComponent('initial');
    setShowModifyOrder(false);
  };

  const onCloseOrderResultModal = () => {
    if (orderInProgress) {
      resetStatus();
    }
    getOrders({
      tenNStatuses_StringList: PENDING_ORDERS_REQUEST_TENNSTATUS,
      isPendingOrdersCall: true,
    });
  };

  const onCloseCancelResultModal = () => {
    onCloseOrderResultModal();
    setShowResultModalCancel(false);
    if (isMessageSummaryAffectingStatus(status)) {
      getMessageSummary();
    }
    SubscribeGroupCache.setComponent('initial');
  };

  const onCloseModifyResultModal = () => {
    onCloseOrderResultModal();
    if (!marketClosed) {
      getOpenPositions();

      if (isMessageSummaryAffectingStatus(status)) {
        getMessageSummary();
      }
    }
    SubscribeGroupCache.setComponent('initial');
    setShowResultModifyModal(false);
    myStocksUpdate(false);
  };

  const handleCloseSelect = () => {
    closeMenu();
  };

  const handleCancelAction = () => {
    onCancelClicked();
    handleCloseSelect();
  };

  const handleModifyAction = () => {
    handleModalModify();
    handleCloseSelect();
  };

  return (
    <>
      {!isMaintenance && (isMobile ? (
        <div className={isSwipeable ? styles.buttonWrapperSwipable : styles.buttonWrapper}>
          <Button
            className={classNames(styles.cancelButtonMobile)}
            variant={ButtonTypesEnum.Link}
            onClick={() => onCancelClicked()}
          >
            <img src={DeleteIcon} alt="cancel icon" />
          </Button>
          <Button
            className={classNames(styles.editButtonMobile)}
            variant={ButtonTypesEnum.Link}
            onClick={() => handleModalModify()}
          >
            <img src={EditIcon} alt="edit icon pink" />
          </Button>
        </div>
      ) : (
          <ClickableDiv style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
            <div className={styles.useDotsButtonsWrapper}>
              <div className={styles.wrapperButtons}>
                {showModifyButtons && (
                  <div className={styles.selectWrapper}>
                    <ClickableImage
                      src={BuyAndSellDot}
                      className={styles.dotTrigger}
                      onClick={openMenu}
                      alt="img"
                    />
                    {isMenuOpen && menuStyle && createPortal(
                      <>
                        <ClickableDiv className={styles.selectBackdrop} onClick={handleCloseSelect} />
                        <div className={styles.selectMenu} style={menuStyle}>
                          <ClickableDiv className={styles.actionButton} onClick={handleCancelAction}>
                            <img src={DeleteIcon} alt="cancel" />
                            <span className={styles.text}>{lang.commonCancelButton()}</span>
                          </ClickableDiv>
                          <ClickableDiv className={styles.actionButton} onClick={handleModifyAction}>
                            <img src={EditIcon} alt="modify" />
                            <span className={styles.text}>{lang.commonModify()}</span>
                          </ClickableDiv>
                        </div>
                      </>,
                      document.body,
                    )}
                  </div>
                )}
              </div>
            </div>
          </ClickableDiv>
      ))}
      {isHalted && (
        <HaltedSymbol symbolName={row.companyName ?? name} toggleModal={() => setIsHalted(false)} />
      )}
      <BaseModal isOpen={showModifyOrder} toggle={handleCloseModifyModal}>
        <ModifyOrder
          symbol={name}
          parentOrderId={id}
          orderSide={orderSide}
          orderType={orderType}
          initialPrice={initialPrice}
          orderQuantity={totalQuantity - partialFillQuantity}
          setOrder={(value) => setOrder(value)}
          setModifyOrderRequested={(value) => {
            setShowModifyOrder(false);
            setShowResultModifyModal(value);
          }}
          onCloseModal={handleCloseModifyModal}
        />
      </BaseModal>
      {showCancelOrderModal && !isMobile && (
        <ProcessFinishedModal
          symbol={name}
          isCancelOrder
          callStatus={orderStatusToCallStatus(status as OrderStatusEnum)}
          processSuccessMessageTitle={lang.orderMessageCancelSuccessTitle(
            getNameByOrderType(orderType),
            convertOrderSideToText(ordSide),
          )}
          processSuccessMessageBody={lang.orderMessageCancelSuccessBody(orderQty ?? 0, name)}
          processFailedMessage={lang.orderMessageBodyCancelFail()}
          processFailedMessageSub={lang.orderPopupFailResultSubTitle()}
          processPendingMessage={
            status === OrderStatusEnum.PendingCancelOrModify
              ? {
                title: lang.orderMessageTitleCancel(
                  getNameByOrderType(orderType),
                  convertOrderSideToText(ordSide),
                ),
                body: lang.orderMessageBodyCancelPending(orderQty!, name),
              }
              : undefined
          }
          defaultMessageTitle={
            orderInProgress
              ? lang.orderMessageTitleCancel(
                getNameByOrderType(orderType),
                convertOrderSideToText(ordSide),
              )
              : ''
          }
          defaultMessageBody={
            orderInProgress ? lang.orderMessageBodyCancelPending(orderQty!, name) : ''
          }
          toggleModal={onCloseCancelResultModal}
        />
      )}
      {showModifyOrderModal && !isMobile && (
        <ProcessFinishedModal
          symbol={name}
          processSuccessMessageTitle={modifyOrderPopUpTitle(
            orderSide,
            showFillOrder,
            name,
            ordType!,
            status,
            order.orderQty,
          )}
          processFailedMessage={lang.orderMessageBodyModifyFail()}
          processFailedMessageSub={lang.orderPopupFailResultSubTitle()}
          processPendingMessage={
            status === OrderStatusEnum.PendingCancelOrModify
              ? {
                title: lang.orderMessageTitleModify2(
                  getNameByOrderType(orderType),
                  convertOrderSideToText(ordSide),
                ),
                body: lang.orderMessageBodyModifyPending2(orderQty!, name),
              }
              : undefined
          }
          processPartiallyRejectedMessage={
            partiallyRejectedOrder
              ? proccessPartiallyRejectedMessage(name, ordType!, ordSide!, orderQty!, filledSoFar)
              : undefined
          }
          processSuccessMessageBody={modifyOrderPopUpBody(
            showFillOrder,
            name,
            filledSoFar,
            status,
            order.orderQty,
          )}
          defaultMessageTitle={
            orderInProgress
              ? lang.orderMessageTitleModify2(
                getNameByOrderType(orderType),
                convertOrderSideToText(ordSide),
              )
              : ''
          }
          defaultMessageBody={
            orderInProgress ? lang.orderMessageBodyModifyPending2(orderQty!, name) : ''
          }
          callStatus={orderStatusToCallStatus(status as OrderStatusEnum)}
          toggleModal={onCloseModifyResultModal}
        />
      )}
      {showModalCancel && (
        <CancelOrder isOpen={showModalCancel} orderId={id} handleModalCancel={handleModalCancel} />
      )}
    </>
  );
};

const mapStateProps = (state: RootState) => ({
  requestStatus: state.trading.requestStatus,
  submittedOrder: state.trading.order,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  resetStatus: () => dispatch(resetRequestStatus()),
  modifyOrder: (order: Order) => dispatch(serviceModifyOrder(order)),
  getOrders: (data: AccountTradeHistoryPayloadData) => dispatch(getAccountTradeHistory(data)),
  getOpenPositions: (symbol?: string) => dispatch(getPositions({ symbol })),
  myStocksUpdate: (hasUpdate: boolean) => dispatch(setHasOrderUpdate(hasUpdate)),
  getMessageSummary: () => dispatch(getSummary()),
  getBuyingPowerFromOMS: () => dispatch(getBuyingPowerFromOMSCall()),
});

const connector = connect(mapStateProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(ButtonsCancelAndModify);
